import Address from './Address';
import Util from './Util';

// keycode which represents a tab-stroke
const KEYCODE_TAB = 9;

/**
 * Represents a global input element for the address-autocomplete. The input-element gets enhanced with the Google-
 * Places autocomplete.
 * It triggers the following Google API events:
 * - 'address_changed': when the address was selected from the places-autocomplete and is therefore verified
 * - 'address_entered': when the address was manually changed but is not verified by the places-autocomplete or the
 *                      address was cleared.
 * - 'input_changed': when the input-element value did change and the address is thus no longer verified or valid.
 */
export default class GlobalAddressInput {

    /**
     * Sets up the Google places autocomplete and the events for the global-input.
     * @param {Element} inputElement the input element
     */
    constructor(inputElement) {
        const that = this;

        this.address = null;
        this.inputElement = inputElement;

        // create the autocomplete object, restricting the search to geographical location types.
        this.autocomplete = new google.maps.places.Autocomplete(this.inputElement, {types: ['geocode']});

        // place was changed through the Google places autocomplete
        this.autocomplete.addListener('place_changed', function() {
            let place = this.getPlace();
            if (!place.hasOwnProperty('address_components') && place.hasOwnProperty('name')) {
                // place was entered manually and is not verified
                let addressString = place.name;
                google.maps.event.trigger(that, 'address_entered', addressString);
            } else {
                // when the user selects an address from the places dropdown
                that.address = Address.fromGeoCoderResult(place);
                google.maps.event.trigger(that, 'address_changed', that.address);
            }
        });

        // address was changed by the input-element
        google.maps.event.addDomListener(this.inputElement, 'input', function() {
            that.address = null;
            if (Util.isEmpty(this.value)) {
                // address was cleared
                google.maps.event.trigger(that, 'address_changed', null);
            } else {
                // input address was changed
                google.maps.event.trigger(that, 'input_changed', this.value);
            }
        });

        // user tabs out of the input field
        google.maps.event.addDomListener(this.inputElement, 'keydown', function(event) {
            let code = event.which || event.keyCode;
            let addressString = this.value;
            if (code === KEYCODE_TAB && !Util.isEmpty(addressString) && !that.address) {
              // address was entered manually without confirming before
              google.maps.event.trigger(that, 'address_entered', addressString);
            }
        });
    }

    /**
     * Set the address for the global input.
     * @param {Address} address the address
     * @returns {null} null
     */
    setAddress(address = null) {
        // check valid address
        if (address instanceof Address) {
            if (!address.equals(this.address)) {
                // set a new address if it changed
                this.address = address;
                this.inputElement.value = address.toString();
            }
        } else {
            // clear address
            this.address = null;
            this.inputElement.value = '';
        }
    }

    /**
     * Set the bounds for the autocomplete where bounds is an instance of google.maps.LatLngBounds.
     * @param {google.maps.LatLngBounds} bounds the google maps bounds
     * @returns {null} null
     */
    setBounds(bounds) {
        this.autocomplete.setBounds(bounds);
    }
}